import constants from "../../assets/constants.js";
import { getConfigurationVersion } from "../../utils/helpers";
import { runBotpressConversation } from "./botpressConversationRunner.js";
import * as BotpressAPI from "./omniBotpressAPI.js";

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const withRetry = async (
  fn,
  { attempts = 3, delayMs = 1500, shouldRetry = () => true } = {},
) => {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt >= attempts || !shouldRetry(error)) {
        break;
      }
      await sleep(delayMs * attempt);
    }
  }
  throw lastError;
};

export const isRetryableRunError = error => !/at least one message/i.test(error?.message || "");

export const buildConfigurationString = configurations => {
  let configuration = "";
  if (configurations && configurations.length > 0) {
    const aiConfigs = configurations.filter(
      config => config.configType === constants.botpressConfigTypeEnums.aiModel,
    );
    for (const theConfig of aiConfigs) {
      configuration += `${theConfig.label}: ${theConfig.value}|`;
    }
  }
  return configuration.length === 0 ? "unknown" : configuration;
};

export const getAssessmentUserName = userODM =>
  userODM ? `${userODM.omniData.givenName} ${userODM.omniData.familyName}` : "Unknown User";

/**
 * Replay a list of questions against a bot and record the run as an Assessment.
 *
 * Both steps are retried independently: a dropped save is what makes a
 * completed run show up with no assessment against it. If the conversation runs
 * but the assessment can't be saved, the returned object still carries the
 * conversation id along with `assessmentError`, so the caller can tell the
 * tester what actually happened rather than reporting a blanket failure.
 *
 * @param {Object}   params
 * @param {Object}   params.botODM             - Bot to replay against and record the assessment on.
 * @param {string[]} params.questions          - Ordered questions to send.
 * @param {Object}   [params.userData]         - Harness user profile to run as.
 * @param {Object}   [params.userODM]          - Logged-in user, stamped on the assessment.
 * @param {Array}    [params.configurations]   - AI config rows, rendered into the config string.
 * @param {Array}    [params.testConfigurations] - Config rows of the run target, for the version.
 * @param {string}   params.assessmentType     - e.g. the template's omniType, or "Custom".
 * @param {string}   params.assessmentName     - Display name for the assessment.
 * @param {string}   [params.comment]          - Free-text comment stored on the assessment.
 * @param {Function} [params.onProgress]       - Forwarded to runBotpressConversation.
 * @returns {Promise<{ conversationId, elapsedSeconds, assessmentCreated, assessmentError }>}
 */
export const runAndRecordAssessment = async ({
  botODM,
  questions,
  userData = null,
  userODM = null,
  configurations = null,
  testConfigurations = null,
  assessmentType,
  assessmentName,
  comment = "",
  onProgress = () => {},
}) => {
  const startedAt = Date.now();

  const result = await withRetry(
    () => runBotpressConversation({ botODM, messages: questions, userData, onProgress }),
    { attempts: 3, shouldRetry: isRetryableRunError },
  );

  const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);

  const assessmentRecord = {
    id: null,
    comment,
    questionCount: questions.length,
    archived: false,
    userName: getAssessmentUserName(userODM),
    conversationId: result.conversationId,
    elapsedSeconds,
    assessmentType,
    assessmentName,
    configuration: buildConfigurationString(configurations),
    version: getConfigurationVersion(testConfigurations),
    status: 0,
  };

  try {
    await withRetry(async () => {
      const assessmentResult = await BotpressAPI.addAssessment({ botODM, assessmentRecord });
      if (assessmentResult && Object.hasOwn(assessmentResult, "code")) {
        throw new Error(assessmentResult.message || "Failed to save assessment");
      }
      return assessmentResult;
    });
    return {
      conversationId: result.conversationId,
      transcript: result.transcript,
      elapsedSeconds,
      assessmentCreated: true,
      assessmentError: null,
    };
  } catch (assessmentError) {
    console.error(`Failed to create assessment "${assessmentName}":`, assessmentError);
    return {
      conversationId: result.conversationId,
      transcript: result.transcript,
      elapsedSeconds,
      assessmentCreated: false,
      assessmentError,
    };
  }
};

export default runAndRecordAssessment;
